import React, { useEffect, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import * as Sentry from '@sentry/browser';
import { useDispatch, useStore } from 'react-redux';
import { useAgent } from 'agents/react';
import { useAgentChat } from '@cloudflare/ai-chat/react';
import { IconButton } from '@mui/material';

import AIAssistantMessages from '../AIAssistantMessages';
import { ReactComponent as CloseIcon } from '../../../../assetsNew/icons/ai/close.svg';
import { ReactComponent as InfoIcon } from '../../../../assetsNew/icons/info.svg';
import { ReactComponent as OpenInFullIcon } from '../../../../assetsNew/icons/ai/north_west.svg';
import { ReactComponent as CloseFullscreenIcon } from '../../../../assetsNew/icons/ai/south_east.svg';
import { ReactComponent as StopCircleIcon } from '../../../../assetsNew/icons/ai/stop_circle.svg';
import { ReactComponent as SendIcon } from '../../../../assetsNew/icons/ai/send-request.svg';
import { ReactComponent as ChatIcon } from '../../../../assetsNew/icons/ai/chat.svg';
import { ReactComponent as ZoomOutMapIcon } from '../../../../assetsNew/icons/ai/zoom_out_map.svg';
import { ReactComponent as SplitLogotype } from '../../../../assetsNew/icons/ai/split-logotype.svg';
import { ReactComponent as CloseDarkIcon } from '../../../../assetsNew/icons/ai/close-dark.svg';
import { ReactComponent as SparklesDarkIcon } from '../../../../assetsNew/icons/ai/sparkles-dark.svg';
import { ReactComponent as SendSmallIcon } from '../../../../assetsNew/icons/ai/send-request-small.svg';
import { AI_AGENT_HOST, ENVIRONMENT } from '../../../../config';
import { getAiChatTokenRequest } from '../../../../sagas/aiChatToken/actions';
import { isAiChatTokenValid } from '../../useAiChatSession';
import { SPLIT_AI_TOKENS_MAILTO } from '../../../../utils/splitContact';

import './_aiAssistantChat.scss';

const TOKEN_LIMIT_MESSAGE = 'It looks like you\'ve run out of tokens to use the bot! If you want to keep the momentum going and unlock unlimited usage, please head over to the settings and upgrade now.';
const FLOATING_DISCLAIMER = 'Anything that spends money or messages a creator becomes a proposal you approve';

// The chat agent closes with 4001 (no token) or 4003 (invalid or expired token). Only these can be fixed
// by a fresh token, so only they remount the chat; it also closes with 4006 (origin) and 4029 (too many
// connections), and the agents client treats every 4xxx close as final.
const AUTH_CLOSE_CODES = [4001, 4003];
const NORMAL_CLOSE_CODES = [1000, 1001, 1005];

// The agents client re-runs the async query on every reconnect, so its cache only has to outlive a session.
// Kept below setTimeout's 2^31 ms limit, which the client schedules the expiry with.
const AGENT_QUERY_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

// Each mount gets its own query cache entry, so a chat remounted after an auth close asks for a fresh token
// rather than reusing the one the agent just rejected.
let agentQueryMountCount = 0;

const getErrorStatusCode = error => error?.statusCode || error?.cause?.statusCode || error?.response?.status || error?.cause?.response?.status;

const getErrorText = error => [
  error?.message,
  error?.cause?.message,
  typeof error?.responseBody === 'string' ? error.responseBody : '',
  typeof error?.cause?.responseBody === 'string' ? error.cause.responseBody : ''
].filter(Boolean).join(' ');

const isTokenLimitError = error => {
  if (!error) return false;

  const statusCode = getErrorStatusCode(error);
  const errorText = getErrorText(error);

  if (statusCode !== 429 && !/\b429\b/.test(errorText)) {
    return false;
  }

  return /(token|usage|quota)/i.test(errorText) && /(limit|run out|exhaust|reached|too many)/i.test(errorText);
};

const AIAssistantChat = ({ avatarUrl, accountUuid, firstName, allowDevAuth, mode, onClose, onModeChange, onAuthExpired, isSmallScreen, isOpen, isExpanded = false, initialPrompt = null, onInitialPromptSent, onTokenLimitReached, submitOnEnter = true }) => {
  const textareaRef = useRef(null);
  const initialPromptSentRef = useRef(false);
  const [draft, setDraft] = React.useState('');
  // A conversation opened with a built-in prompt (e.g. "Review Suggestion") hides the suggested prompts
  const [startedFromPrompt, setStartedFromPrompt] = React.useState(false);
  const [evalStatus, setEvalStatus] = React.useState(null);
  const [bufferStatus, setBufferStatus] = React.useState(null);
  const dispatch = useDispatch();
  const store = useStore();
  const isDevAuth = allowDevAuth && ENVIRONMENT === 'development';
  const [agentQueryMountKey] = useState(() => {
    agentQueryMountCount += 1;
    return agentQueryMountCount;
  });
  // The agent name and the dev-mode query are read once per mount. The JWT is not: see getAgentQuery.
  const frozenNameRef = useRef(null);
  const frozenQueryRef = useRef(null);
  if (frozenQueryRef.current === null) {
    const cookieToken = isDevAuth
      ? Cookies.get('bsn_tkn') || ''
      : Cookies.get('ai_chat_token') || '';
    const isJwt = cookieToken.startsWith('eyJ') && cookieToken.split('.').length === 3;
    let name = '';
    if (isJwt) {
      try {
        const payloadB64 = cookieToken.split('.')[1];
        const padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/') +
          '=='.slice(0, (4 - (payloadB64.length % 4)) % 4);
        const payload = JSON.parse(atob(padded));
        name = payload.sub || '';
      } catch {
        // fall through
      }
    }
    frozenNameRef.current = name || (isDevAuth ? accountUuid : null) || null;
    frozenQueryRef.current = isDevAuth ? { token: cookieToken } : {};
  }

  // Asked on connect and again on every reconnect, so a socket that drops after the token expired comes
  // back with a current one (refreshed by the saga when it's close to expiry) instead of the mount-time one.
  // Without a token it connects bare, and the agent's 4001 close remounts the chat once a new token lands.
  const getAgentQuery = useCallback(() => {
    const { token, expiresAt } = store.getState().aiChatToken || {};
    if (token && isAiChatTokenValid(expiresAt)) return Promise.resolve({ jwt: token });

    return new Promise(resolve => {
      dispatch(getAiChatTokenRequest({
        onSuccess: freshToken => resolve(freshToken ? { jwt: freshToken } : {}),
        onError: () => resolve({})
      }));
    });
  }, [dispatch, store]);

  const handleAgentClose = useCallback(event => {
    const isAuthClose = AUTH_CLOSE_CODES.includes(event.code);
    const isNormalClose = NORMAL_CLOSE_CODES.includes(event.code);
    const closeInfo = { code: event.code, reason: event.reason, wasClean: event.wasClean, mode };

    if (isNormalClose) {
      console.info('[AIAssistantChat] socket closed', closeInfo);
    } else {
      console.warn('[AIAssistantChat] socket closed', closeInfo);
    }

    Sentry.addBreadcrumb({ category: 'ai-assistant', message: `Chat socket closed (${event.code})`, data: closeInfo, level: isNormalClose ? 'info' : 'warning' });

    // A close the client won't recover from (4xxx other than auth, or 1008) leaves the chat dead, so it's reported.
    const isFinalClose = event.code === 1008 || (event.code >= 4000 && event.code <= 4999);
    if (isFinalClose && !isAuthClose) {
      Sentry.captureMessage(`AI Assistant chat socket closed (${event.code})`, { level: 'warning', tags: { feature: 'ai-assistant' }, extra: closeInfo });
    }

    if (isAuthClose) {
      onAuthExpired();
    }
  }, [mode, onAuthExpired]);

  const agent = useAgent({
    agent: 'ChatAgent',
    name: frozenNameRef.current || undefined,
    host: isDevAuth ? 'localhost:8787' : AI_AGENT_HOST,
    query: isDevAuth ? frozenQueryRef.current : getAgentQuery,
    queryDeps: [agentQueryMountKey],
    cacheTtl: AGENT_QUERY_CACHE_TTL_MS,
    onClose: handleAgentClose,
    onMessage: event => {
      if (typeof event?.data !== 'string') return;
      try {
        const message = JSON.parse(event.data);
        if (message?.type === 'evaluation-status') {
          setEvalStatus(message.state === 'checking' || message.state === 'repairing' ? message.state : null);
        }
      } catch {
        // Ignore non-JSON websocket frames.
      }
    }
  });

  const {
    messages,
    sendMessage,
    addToolApprovalResponse,
    clearHistory,
    error,
    stop,
    status,
    isStreaming: isChatStreaming,
    isRecovering
  } = useAgentChat({
    agent,
    onData: part => {
      if (part?.type === 'data-status' && part?.data?.text) {
        setBufferStatus(part.data.text);
      }
    }
  });

  useEffect(() => {
    if (status !== 'streaming') {
      setBufferStatus(null);
    }
  }, [status]);

  useEffect(() => {
    if (!evalStatus) return undefined;

    const timeoutId = setTimeout(() => setEvalStatus(null), 180000);
    return () => clearTimeout(timeoutId);
  }, [evalStatus]);

  useEffect(() => {
    if (!textareaRef.current) {
      return;
    }

    const textarea = textareaRef.current;
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 20;
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    const borderTop = parseFloat(computedStyle.borderTopWidth) || 0;
    const borderBottom = parseFloat(computedStyle.borderBottomWidth) || 0;
    const maxHeight = Math.ceil((lineHeight * 3) + paddingTop + paddingBottom + borderTop + borderBottom);

    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [draft, mode]);

  useEffect(() => {
    if (isOpen && mode === 'floating' && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen, mode]);

  // The socket queues messages until it connects, so the prompt can be sent right after mount.
  // The chat stays mounted between prompts (e.g. the global assistant is only hidden when
  // closed), so the "sent" flag resets once the parent clears the prompt.
  useEffect(() => {
    if (!initialPrompt) {
      initialPromptSentRef.current = false;
      return;
    }

    if (initialPromptSentRef.current) return;

    initialPromptSentRef.current = true;
    setStartedFromPrompt(true);
    sendMessage({ text: initialPrompt });
    onInitialPromptSent && onInitialPromptSent();
  }, [initialPrompt]); // eslint-disable-line

  const handleSubmit = event => {
    event.preventDefault();

    if (isStreaming || evalStatus) return;

    const nextDraft = draft.trim();
    if (!nextDraft) return;

    sendMessage({ text: nextDraft });

    setDraft('');
  };

  const handleStopGeneration = useCallback(() => {
    stop();
  }, [stop]);

  const handleResetConversation = useCallback(() => {
    stop();
    clearHistory();
    setDraft('');
    setStartedFromPrompt(false);
  }, [clearHistory, stop]);

  // Includes a reply the server streams after a reconnect or remount, and a turn it is recovering,
  // so Stop stays available and a new message can't be sent over it.
  const isStreaming = isChatStreaming || isRecovering;
  const hasTokenLimitError = isTokenLimitError(error);

  useEffect(() => {
    if (!error || hasTokenLimitError) return;

    console.error('[AIAssistantChat] chat error', error);
    Sentry.captureException(error, { tags: { feature: 'ai-assistant' }, extra: { mode, status } });
  }, [error]); // eslint-disable-line

  useEffect(() => {
    if (hasTokenLimitError && onTokenLimitReached) onTokenLimitReached();
  }, [hasTokenLimitError]); // eslint-disable-line

  // Without submitOnEnter, Enter only adds a new line and the message goes out with the send button
  const handleComposerKeyDown = event => {
    if (submitOnEnter && event.key === 'Enter' && !event.shiftKey) {
      handleSubmit(event);
    }
  };

  const isFloating = mode === 'floating';
  const isPanel = mode === 'panel';
  const hasCompactComposer = mode === 'sidebar' || isPanel;

  const sectionClassName = [
    'aiAssistantChat',
    hasTokenLimitError && 'aiAssistantChat--limitError',
    isFloating && 'aiAssistantChat--floating',
    isPanel && 'aiAssistantChat--panel'
  ].filter(Boolean).join(' ');

  return (
    <section className={sectionClassName}>
      {isFloating ? (
        <header className="aiAssistantChat-header aiAssistantChat-header--floating">
          <IconButton className="aiAssistantChat-floatingExpand" onClick={onModeChange} aria-label={isExpanded ? 'Collapse window' : 'Expand window'}>
            <ZoomOutMapIcon />
          </IconButton>
          <div className="aiAssistantChat-floatingLogo">
            <SplitLogotype />
            <span className="aiAssistantChat-floatingLogoLabel">Agent</span>
          </div>
          <div className="aiAssistantChat-floatingActions">
            <button className="aiAssistantChat-floatingReset" onClick={handleResetConversation} type="button">
              Reset
            </button>
            <IconButton className="aiAssistantChat-floatingClose" onClick={onClose} aria-label="Close AI assistant">
              <CloseDarkIcon />
            </IconButton>
          </div>
        </header>
      ) : isPanel ? (
        <header className="aiAssistantChat-header aiAssistantChat-header--panel">
          <IconButton className="aiAssistantChat-panelExpand" onClick={onModeChange} aria-label="Expand to full screen">
            <ZoomOutMapIcon />
          </IconButton>
        </header>
      ) : (
        <header className="aiAssistantChat-header">
          <div className="aiAssistantChat-headerActions">
            <IconButton className="aiAssistantChat-iconButton" onClick={onModeChange}>
              {mode === 'sidebar' ? <OpenInFullIcon /> : <CloseFullscreenIcon />}
            </IconButton>
            <div className="aiAssistantChat-headerActions-right">
              <IconButton className="aiAssistantChat-iconButton clear" onClick={handleResetConversation} title="Reset conversation">
                Reset
              </IconButton>
              <IconButton className="aiAssistantChat-iconButton close" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </header>
      )}

      <AIAssistantMessages
        messages={messages}
        sendMessage={sendMessage}
        addToolApprovalResponse={addToolApprovalResponse}
        avatarUrl={avatarUrl}
        firstName={firstName}
        hideSuggestions={hasTokenLimitError || startedFromPrompt}
        status={isStreaming ? 'streaming' : status}
        evaluationStatus={evalStatus}
        bufferStatus={bufferStatus}
        isOpen={isOpen}
        hasDraft={Boolean(draft.trim())}
        mode={mode}
      />

      {hasTokenLimitError ? (
        <div className={hasCompactComposer ? 'aiAssistantChat-limitState aiAssistantChat-limitState--sidebar' : 'aiAssistantChat-limitState'}>
          <div className="aiAssistantChat-limitInfo">
            <InfoIcon className="aiAssistantChat-limitInfoIcon" />
            <p className="aiAssistantChat-limitText">{TOKEN_LIMIT_MESSAGE}</p>
          </div>
          <a className="aiAssistantChat-limitButton" href={SPLIT_AI_TOKENS_MAILTO}>
            Talk to Split
            <ChatIcon />
          </a>
        </div>
      ) : isFloating ? (
        <div className="aiAssistantChat-floatingFooter">
          <form className="aiAssistantChat-composer aiAssistantChat-composer--floating" onSubmit={handleSubmit}>
            <span className="aiAssistantChat-floatingBadge">
              <SparklesDarkIcon />
            </span>
            <textarea
              className="aiAssistantChat-textarea aiAssistantChat-textarea--floating"
              ref={textareaRef}
              onChange={event => setDraft(event.target.value)}
              onKeyDown={handleComposerKeyDown}
              placeholder="Ask for insights or next steps…"
              rows={1}
              value={draft}
            />
            {isStreaming ? (
              <button
                aria-label="Stop generation"
                className="aiAssistantChat-floatingSend aiAssistantChat-floatingSend--stop"
                onClick={handleStopGeneration}
                type="button"
              >
                <StopCircleIcon />
              </button>
            ) : (
              <button
                aria-label="Send message"
                className="aiAssistantChat-floatingSend"
                disabled={!draft.trim() || Boolean(evalStatus)}
                type="submit"
              >
                <SendSmallIcon />
              </button>
            )}
          </form>
          <p className="aiAssistantChat-floatingDisclaimer">{FLOATING_DISCLAIMER}</p>
        </div>
      ) : (
        <form className="aiAssistantChat-composer" onSubmit={handleSubmit}>
          <textarea
            className="aiAssistantChat-textarea"
            ref={textareaRef}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={handleComposerKeyDown}
            placeholder="Ask anything"
            rows={1}
            value={draft}
          />
          {hasCompactComposer && isStreaming && (
            <button
              aria-label="Stop generation"
              className="aiAssistantChat-stop aiAssistantChat-stop--iconOnly"
              onClick={handleStopGeneration}
              type="button"
            >
              <StopCircleIcon />
            </button>
          )}
          {hasCompactComposer && !isStreaming && (
            <button
              aria-label="Send message"
              className="aiAssistantChat-send aiAssistantChat-send--iconOnly"
              disabled={!draft.trim() || evalStatus}
              type="submit"
            >
              <SendIcon />
            </button>
          )}
          {mode === 'fullscreen' && !isSmallScreen && (
            isStreaming ? (
              <button className="aiAssistantChat-stop" onClick={handleStopGeneration} type="button">
                Stop
                <StopCircleIcon />
              </button>
            ) : (
              <button className="aiAssistantChat-send" disabled={!draft.trim() || evalStatus} type="submit">
                Send
                <SendIcon />
              </button>
            )
          )}
        </form>
      )}
    </section>
  );
};

AIAssistantChat.propTypes = {
  avatarUrl: PropTypes.string,
  accountUuid: PropTypes.string,
  firstName: PropTypes.string,
  allowDevAuth: PropTypes.bool,
  mode: PropTypes.oneOf(['sidebar', 'fullscreen', 'floating', 'panel']).isRequired,
  onClose: PropTypes.func.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onAuthExpired: PropTypes.func.isRequired,
  isSmallScreen: PropTypes.bool,
  isOpen: PropTypes.bool,
  isExpanded: PropTypes.bool,
  initialPrompt: PropTypes.string,
  onInitialPromptSent: PropTypes.func,
  onTokenLimitReached: PropTypes.func,
  submitOnEnter: PropTypes.bool
};

export default AIAssistantChat;
